import { Box, Chip, Tooltip } from "@mui/material";
import { DocumentReviewProjection } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { deductibleSourceLabel } from "@/components/ui/mui/DeductibleSourceChip";

interface DeductibleContextChipsProps {
  projection: DocumentReviewProjection | null;
}

/**
 * Compact chip row shown in the review toolbar that surfaces the applicable
 * deductibles the forecast is running against. Gives reviewers a quick "what
 * threshold is each YTD being compared to?" reference without opening a policy.
 *
 * Renders nothing when the projection hasn't loaded or there's no deductible
 * to report — absence is intentional, not a loading state.
 *
 * Family and specific deductibles are mutually exclusive at the policy level
 * per the business rule. When family is active, only the family chip renders —
 * applicable_deductible and family_deductible carry the same amount in that
 * case, so rendering both would be redundant noise.
 */
export const DeductibleContextChips = ({ projection }: DeductibleContextChipsProps) => {
  if (!projection) return null;

  const hasApplicable = typeof projection.applicable_deductible === "number" && projection.applicable_deductible > 0;
  const hasFamily = typeof projection.family_deductible === "number" && projection.family_deductible > 0;
  if (!hasApplicable && !hasFamily) return null;

  // When family is the applicable source, family_deductible is redundant with
  // applicable_deductible — suppress the separate specific/applicable chip and
  // render one family chip.
  const familyIsApplicable = projection.deductible_source === "family";
  const showApplicable = hasApplicable && !familyIsApplicable;

  return (
    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
      {showApplicable && (
        <Tooltip
          title={`Resolved from the client's active policy (${
            deductibleSourceLabel(projection.deductible_source) || "applicable"
          }). Each claimant's YTD is compared against this to determine breach.`}
          placement="top"
          arrow
        >
          <Chip
            label={`${deductibleSourceLabel(projection.deductible_source) || "Applicable"} deductible: ${formatUSD(projection.applicable_deductible)}`}
            size="small"
            variant="outlined"
            sx={{ height: 24, "& .MuiChip-label": { px: 1, fontSize: "0.75rem" } }}
          />
        </Tooltip>
      )}
      {hasFamily && typeof projection.family_deductible === "number" && (
        <Tooltip
          title="Family deductible endorsement is active on this policy. Every claimant — solo or in a family — is evaluated against this threshold; specific deductible does not apply."
          placement="top"
          arrow
        >
          <Chip
            label={`Family deductible: ${formatUSD(projection.family_deductible)}`}
            size="small"
            variant="outlined"
            sx={{ height: 24, "& .MuiChip-label": { px: 1, fontSize: "0.75rem" } }}
          />
        </Tooltip>
      )}
    </Box>
  );
};
